import React, { useEffect, useState } from 'react'
import { supabase } from '../../lib/supabaseClient'

type SlotType = 'PICKUP' | 'DELIVERY'
type BlackoutType = 'PICKUP' | 'DELIVERY' | 'BOTH'

type WeeklySlot = {
  id: string
  slot_type: SlotType
  weekday: number // 1 = lundi, 7 = dimanche
  start_time: string
  end_time: string
  is_active: boolean
}

type Blackout = {
  id: string
  slot_type: BlackoutType | null
  start_date: string
  end_date: string
  reason: string | null
}

const weekdayLabels: Record<number, string> = {
  1: 'Lundi',
  2: 'Mardi',
  3: 'Mercredi',
  4: 'Jeudi',
  5: 'Vendredi',
  6: 'Samedi',
  7: 'Dimanche'
}

const AdminSlotsPage: React.FC = () => {
  const [weeklySlots, setWeeklySlots] = useState<WeeklySlot[]>([])
  const [blackouts, setBlackouts] = useState<Blackout[]>([])
  const [loading, setLoading] = useState(true)

  // Formulaire création plage hebdo
  const [wsType, setWsType] = useState<SlotType>('PICKUP')
  const [wsWeekday, setWsWeekday] = useState<number>(1)
  const [wsStart, setWsStart] = useState('17:00')
  const [wsEnd, setWsEnd] = useState('19:00')

  // Formulaire création période d’indispo
  const [boType, setBoType] = useState<BlackoutType>('BOTH')
  const [boStartDate, setBoStartDate] = useState('')
  const [boEndDate, setBoEndDate] = useState('')
  const [boReason, setBoReason] = useState('Vacances')

  const load = async () => {
    setLoading(true)

    const [{ data: wsData, error: wsError }, { data: boData, error: boError }] =
      await Promise.all([
        supabase
          .from('weekly_slots')
          .select('*')
          .order('weekday', { ascending: true })
          .order('start_time', { ascending: true }),
        supabase
          .from('slot_blackouts')
          .select('*')
          .order('start_date', { ascending: true })
      ])

    if (wsError) console.error(wsError)
    if (boError) console.error(boError)

    setWeeklySlots((wsData ?? []) as WeeklySlot[])
    setBlackouts((boData ?? []) as Blackout[])

    setLoading(false)
  }

  useEffect(() => {
    void load()
  }, [])

  // --- Gestion plages hebdo ---

  const handleCreateWeeklySlot = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!wsStart || !wsEnd) {
      alert('Merci de renseigner une heure de début et de fin.')
      return
    }

    const { error } = await supabase.from('weekly_slots').insert({
      slot_type: wsType,
      weekday: wsWeekday,
      start_time: wsStart,
      end_time: wsEnd,
      is_active: true
    })

    if (error) {
      console.error(error)
      alert("Erreur lors de la création de la plage hebdomadaire.")
      return
    }

    setWsWeekday(1)
    setWsStart('17:00')
    setWsEnd('19:00')
    setWsType('PICKUP')
    void load()
  }

  const toggleWeeklySlotActive = async (slot: WeeklySlot) => {
    const { error } = await supabase
      .from('weekly_slots')
      .update({ is_active: !slot.is_active })
      .eq('id', slot.id)

    if (error) {
      console.error(error)
      alert("Erreur lors de la mise à jour de la plage.")
      return
    }

    setWeeklySlots(prev =>
      prev.map(s => (s.id === slot.id ? { ...s, is_active: !slot.is_active } : s))
    )
  }

  const deleteWeeklySlot = async (slot: WeeklySlot) => {
    const ok = window.confirm(
      `Supprimer la plage ${weekdayLabels[slot.weekday]} ${slot.start_time}–${slot.end_time} (${slot.slot_type === 'PICKUP' ? 'prise en charge' : 'livraison'}) ?`
    )
    if (!ok) return

    const { error } = await supabase.from('weekly_slots').delete().eq('id', slot.id)
    if (error) {
      console.error(error)
      alert("Impossible de supprimer cette plage.")
      return
    }

    setWeeklySlots(prev => prev.filter(s => s.id !== slot.id))
  }

  // --- Gestion périodes d’indispo ---

  const handleCreateBlackout = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!boStartDate || !boEndDate) {
      alert('Merci de renseigner la date de début et de fin.')
      return
    }

    const start = new Date(boStartDate)
    const end = new Date(boEndDate)
    if (end < start) {
      alert('La date de fin doit être postérieure à la date de début.')
      return
    }

    const { error } = await supabase.from('slot_blackouts').insert({
      slot_type: boType,
      start_date: boStartDate,
      end_date: boEndDate,
      reason: boReason || null
    })

    if (error) {
      console.error(error)
      alert("Erreur lors de la création de la période d’indisponibilité.")
      return
    }

    setBoStartDate('')
    setBoEndDate('')
    setBoReason('Vacances')
    setBoType('BOTH')
    void load()
  }

  const deleteBlackout = async (blackout: Blackout) => {
    const labelType =
      blackout.slot_type === 'PICKUP'
        ? 'prises en charge'
        : blackout.slot_type === 'DELIVERY'
          ? 'livraisons'
          : 'prises en charge & livraisons'

    const ok = window.confirm(
      `Supprimer la période d’indisponibilité du ${blackout.start_date} au ${blackout.end_date} (${labelType}) ?`
    )
    if (!ok) return

    const { error } = await supabase.from('slot_blackouts').delete().eq('id', blackout.id)
    if (error) {
      console.error(error)
      alert("Impossible de supprimer cette période d’indisponibilité.")
      return
    }

    setBlackouts(prev => prev.filter(b => b.id !== blackout.id))
  }

  // --- Render helpers ---

  const renderWeeklySlot = (slot: WeeklySlot) => {
    const labelType = slot.slot_type === 'PICKUP' ? 'Prise en charge' : 'Livraison'
    return (
      <div
        key={slot.id}
        style={{
          borderTop: '1px solid rgba(148,163,184,.4)',
          padding: '.75rem 0',
          display: 'flex',
          justifyContent: 'space-between',
          gap: '.75rem',
          alignItems: 'center'
        }}
      >
        <div>
          <div style={{ fontWeight: 600 }}>
            [{labelType}] {weekdayLabels[slot.weekday]}
          </div>
          <div style={{ fontSize: '.85rem', color: '#e5e7eb' }}>
            {slot.start_time} – {slot.end_time}
          </div>
        </div>
        <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', gap: '.25rem' }}>
          <div className="chip">
            <span className="chip-dot" />
            {slot.is_active ? 'Actif' : 'Inactif'}
          </div>
          <div style={{ display: 'flex', gap: '.25rem', justifyContent: 'flex-end' }}>
            <button
              type="button"
              data-testid={`weekly-slot-toggle-${slot.id}`}
              className="btn"
              onClick={() => toggleWeeklySlotActive(slot)}
            >
              {slot.is_active ? 'Désactiver' : 'Activer'}
            </button>
            <button
              type="button"
              data-testid={`weekly-slot-delete-${slot.id}`}
              className="btn btn-ghost"
              onClick={() => deleteWeeklySlot(slot)}
            >
              Supprimer
            </button>
          </div>
        </div>
      </div>
    )
  }

  const renderBlackout = (blackout: Blackout) => {
    const labelType =
      blackout.slot_type === 'PICKUP'
        ? 'Prises en charge'
        : blackout.slot_type === 'DELIVERY'
          ? 'Livraisons'
          : 'Prises en charge & livraisons'

    return (
      <div
        key={blackout.id}
        style={{
          borderTop: '1px solid rgba(148,163,184,.4)',
          padding: '.75rem 0',
          display: 'flex',
          justifyContent: 'space-between',
          gap: '.75rem',
          alignItems: 'center'
        }}
      >
        <div>
          <div style={{ fontWeight: 600 }}>
            {blackout.start_date} → {blackout.end_date}
          </div>
          <div style={{ fontSize: '.8rem', color: '#9ca3af' }}>{labelType}</div>
          {blackout.reason && (
            <div style={{ fontSize: '.8rem', marginTop: '.25rem' }}>
              Motif : {blackout.reason}
            </div>
          )}
        </div>
        <div>
          <button
            type="button"
            data-testid={`blackout-delete-${blackout.id}`}
            className="btn btn-ghost"
            onClick={() => deleteBlackout(blackout)}
          >
            Supprimer
          </button>
        </div>
      </div>
    )
  }

  return (
    <div>
      <h2 className="card-title">Créneaux de prise en charge & livraison</h2>

      {loading && <p>Chargement…</p>}

      {!loading && (
        <>
          {/* Plages hebdomadaires */}
          <section style={{ marginBottom: '2rem' }}>
            <h3 style={{ fontSize: '.95rem', marginBottom: '.5rem' }}>Plages récurrentes (par jour de la semaine)</h3>

            <form onSubmit={handleCreateWeeklySlot} style={{ marginBottom: '1rem' }}>
              <div className="grid-3" style={{ gap: '.75rem' }}>
                <div>
                  <label className="label">Type</label>
                  <select
                    className="select"
                    value={wsType}
                    onChange={e => setWsType(e.target.value as SlotType)}
                  >
                    <option value="PICKUP">Prise en charge</option>
                    <option value="DELIVERY">Livraison</option>
                  </select>
                </div>
                <div>
                  <label className="label">Jour</label>
                  <select
                    className="select"
                    value={wsWeekday}
                    onChange={e => setWsWeekday(Number(e.target.value))}
                  >
                    {Object.entries(weekdayLabels).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
                <div />
              </div>

              <div className="grid-3" style={{ gap: '.75rem', marginTop: '.5rem' }}>
                <div>
                  <label className="label">Heure de début</label>
                  <input
                    className="input"
                    type="time"
                    value={wsStart}
                    onChange={e => setWsStart(e.target.value)}
                  />
                </div>
                <div>
                  <label className="label">Heure de fin</label>
                  <input
                    className="input"
                    type="time"
                    value={wsEnd}
                    onChange={e => setWsEnd(e.target.value)}
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <button className="btn btn-primary" type="submit">
                    Ajouter la plage
                  </button>
                </div>
              </div>
            </form>

            {weeklySlots.length === 0 && (
              <p style={{ fontSize: '.85rem', color: '#9ca3af' }}>
                Aucune plage hebdomadaire configurée.
              </p>
            )}
            {weeklySlots.map(renderWeeklySlot)}
          </section>

          {/* Périodes d’indisponibilité */}
          <section>
            <h3 style={{ fontSize: '.95rem', marginBottom: '.5rem' }}>
              Périodes d’indisponibilité (vacances, fermetures)
            </h3>

            <form onSubmit={handleCreateBlackout} style={{ marginBottom: '1rem' }}>
              <div className="grid-3" style={{ gap: '.75rem' }}>
                <div>
                  <label className="label">S’applique à</label>
                  <select
                    className="select"
                    value={boType}
                    onChange={e => setBoType(e.target.value as BlackoutType)}
                  >
                    <option value="BOTH">Prise en charge & livraison</option>
                    <option value="PICKUP">Seulement prise en charge</option>
                    <option value="DELIVERY">Seulement livraison</option>
                  </select>
                </div>
                <div>
                  <label className="label">Date de début</label>
                  <input
                    className="input"
                    type="date"
                    value={boStartDate}
                    onChange={e => setBoStartDate(e.target.value)}
                  />
                </div>
                <div>
                  <label className="label">Date de fin</label>
                  <input
                    className="input"
                    type="date"
                    value={boEndDate}
                    onChange={e => setBoEndDate(e.target.value)}
                  />
                </div>
              </div>

              <div style={{ marginTop: '.5rem', marginBottom: '.5rem' }}>
                <label className="label">Motif (optionnel)</label>
                <input
                  className="input"
                  value={boReason}
                  onChange={e => setBoReason(e.target.value)}
                  placeholder="Vacances d'été, tournoi, etc."
                />
              </div>

              <button className="btn btn-primary" type="submit">
                Ajouter la période d’indisponibilité
              </button>
            </form>

            {blackouts.length === 0 && (
              <p style={{ fontSize: '.85rem', color: '#9ca3af' }}>
                Aucune période d’indisponibilité configurée.
              </p>
            )}
            {blackouts.map(renderBlackout)}
          </section>
        </>
      )}
    </div>
  )
}

export default AdminSlotsPage
